import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Privacy() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#F7F5F2] px-4 py-8 sm:py-14">
      <div className="max-w-3xl mx-auto">
        <Link
          to={user ? "/" : "/"}
          data-testid="privacy-back"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B2A4A]/70 hover:text-[#E8621A] transition-colors duration-200 mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="rounded-3xl bg-white border border-[#1B2A4A]/8 p-6 sm:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#2A9D8F]/10 text-[#2A9D8F] px-3 py-1 text-xs font-semibold tracking-widest uppercase">
            <Shield className="w-3 h-3" /> Privacy Policy
          </div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold text-[#1B2A4A] leading-tight">
            Surviving Together: <span className="text-[#E8621A]">Thriving Together</span>
          </h1>
          <p className="mt-3 text-sm text-[#1B2A4A]/60">Last updated: October 10, 2026</p>

          <div className="mt-8 space-y-6 text-[#1B2A4A]/85 leading-relaxed">
            <p>
              Surviving Together is run by SocialImpactCLE, Cleveland, Ohio. Contact:{" "}
              <a href="mailto:info@survivingtogether.org" className="text-[#E8621A] font-semibold hover:underline">
                info@survivingtogether.org
              </a>
            </p>

            <section>
              <h2 className="font-heading text-xl font-bold text-[#1B2A4A] mb-2">What we collect</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Account details: name, email, ZIP code, and password (stored scrambled, never readable).</li>
                <li>What you add: profile photo, tips, comments, likes, and budget entries you save in Simple Budget.</li>
                <li>Optional choices: the newsletter, and whether we may feature your first name in it.</li>
                <li>Basic usage, such as badges earned and Relaxation Room visits.</li>
                <li>Payments are handled by Stripe. We never see or store your card number.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold text-[#1B2A4A] mb-2">How we use it</h2>
              <p>
                To run the community, show your tips and profile to other members, send the newsletter only if you opted in,
                process memberships, keep the community safe, and improve the app.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold text-[#1B2A4A] mb-2">What we don't do</h2>
              <p>We do not sell your data, run ads, or share your information with advertisers.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold text-[#1B2A4A] mb-2">Who sees what</h2>
              <p>
                Other members can see your name, ZIP, photo, tips and badges. Your email and budget entries are private.
                Administrators can see account details to keep the community safe.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold text-[#1B2A4A] mb-2">Service providers</h2>
              <p>Stripe (payments), MongoDB Atlas (database) and Emergent (hosting).</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold text-[#1B2A4A] mb-2">Your choices</h2>
              <p>
                You can change your password and email in your profile, unsubscribe from the newsletter at any time, and ask us
                to delete your account and data at{" "}
                <a href="mailto:info@survivingtogether.org" className="text-[#E8621A] font-semibold hover:underline">
                  info@survivingtogether.org
                </a>
                . We'll respond within 30 days.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold text-[#1B2A4A] mb-2">Age</h2>
              <p>This service is for people 18 and older.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold text-[#1B2A4A] mb-2">Changes</h2>
              <p>If we change this policy, we'll update the date above and notify members of major changes.</p>
            </section>
          </div>

          <div className="mt-10 pt-6 border-t border-[#1B2A4A]/10 flex flex-wrap gap-4 text-sm">
            <Link to="/terms" data-testid="privacy-link-terms" className="text-[#E8621A] font-semibold hover:underline">
              Terms of Service
            </Link>
            <Link to="/guidelines" data-testid="privacy-link-guidelines" className="text-[#E8621A] font-semibold hover:underline">
              Community Guidelines
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
